import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Page } from '/imports/ui/Page';
import { PageBody } from '/imports/ui/PageBody';
import { PageHeading } from '/imports/ui/PageHeading';
import { Panel } from '/imports/ui/Panel';
import { Stat, StatRow } from '/imports/ui/Stat';
import { Badge, BadgeTone } from '/imports/ui/Badge';
import { ChipGroup } from '/imports/ui/ChipGroup';
import { Icon } from '/imports/ui/Icon';
import { Input } from '/imports/ui/Input';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { useMethod, useUser } from '/imports/hooks';
import {
  getAcquisitionStats, getReliabilityStats, getInventoryValue, getOverviewStats, getOverviewProfitLifo, getTradeLog,
  getProfitByCard, getCardLines, AcquisitionStats, OverviewStats, OverviewArg, TradeProfitLifo, TradeSessionRow,
} from '/imports/api/analytics';
import { pctDelta, compareLabel } from '/imports/api/overview';
import { useChartTheme, ChartTheme } from '/imports/ui/chartTheme';
import { kindVocab, DIR_LABEL, fmtInt, fmtTix, fmtSigned, fmtPrice, fmtPct, hhmm, ymd } from '/imports/ui/tradeVocab';

interface RangeChoice {
  id: string;
  label: string;
  /** 0 = today, from the viewer's local midnight; null = all time. */
  days: number | null;
}

const RANGES: RangeChoice[] = [
  { id: 'today', label: 'Today', days: 0 },
  { id: '7d', label: '7d', days: 7 },
  { id: '30d', label: '30d', days: 30 },
  { id: '90d', label: '90d', days: 90 },
  { id: 'all', label: 'All', days: null },
];
const DEFAULT_RANGE = '7d';
const rangeTitle = (r: RangeChoice) => (r.days === 0 ? 'Today' : r.days === null ? 'All time' : `Last ${r.label}`);

/** The viewer's local midnight: "today" is a local day, not a rolling 24 hours. */
const localMidnight = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};
const viewerTz = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const fmtDate = (d?: Date | null) => (d ? ymd(d) : '--');
const agoShort = (d: Date | string) => {
  const min = Math.max(0, Math.round((Date.now() - new Date(d).getTime()) / 60000));
  return min < 60 ? `${min}m ago` : `${Math.floor(min / 60)}h ago`;
};

const statusTone: Record<string, BadgeTone> = {
  running: 'green', inTrade: 'blue', connecting: 'amber', loggingIn: 'amber',
  restarting: 'amber', stalled: 'gold', crashed: 'red', offline: 'neutral',
};

type Drill = 'profit' | 'cardsBought' | 'cardsSold';
const DRILLS: Drill[] = ['profit', 'cardsBought', 'cardsSold'];

const ChartTooltip = ({ theme }: { theme: ChartTheme }) => (
  <Tooltip
    contentStyle={{
      background: theme.panel,
      border: `1px solid ${theme.grid}`,
      borderRadius: 6,
      fontSize: 12,
      color: theme.text,
    }}
    labelStyle={{ color: theme.axis, fontSize: 11 }}
    formatter={(value: any, name: any) => [fmtInt(Number(value)), name]}
  />
);

const EmptyRange = ({ stats }: { stats: AcquisitionStats }) => (
  <div className="flex flex-col items-center gap-1 py-14 text-center">
    <div className="text-[13px] font-semibold text-fg-1">No trades in this range</div>
    <div className="text-[12px] text-fg-3">Last recorded activity: {fmtDate(stats.lastActivityEver)}.</div>
  </div>
);

export const DashboardPage = () => {
  const user = useUser();
  const [params, setParams] = useSearchParams();
  const [rangeId, setRangeId] = useState(DEFAULT_RANGE);
  const theme = useChartTheme(user?.settings?.theme);
  const range = RANGES.find((r) => r.id === rangeId) || RANGES[1];
  const drillParam = params.get('drill');
  const drill = drillParam && (DRILLS as string[]).includes(drillParam) ? (drillParam as Drill) : null;

  const overviewArg = useMemo((): OverviewArg => ({ days: range.days, todayStart: localMidnight(), tz: viewerTz() }), [rangeId]);
  const acquisitionArg = useMemo(() => {
    if (range.days === null) return {};
    const to = new Date();
    const from = range.days === 0 ? localMidnight() : new Date(to.getTime() - range.days * 86400000);
    return { from, to };
  }, [rangeId]);

  const [overview, overviewLoading] = useMethod(getOverviewStats, overviewArg, [rangeId]);
  const [stats, loading] = useMethod(getAcquisitionStats, acquisitionArg, [rangeId]);
  const [reliability] = useMethod(getReliabilityStats, { days: 30 }, []);
  const [inventory] = useMethod(getInventoryValue, {}, []);

  if (!user?.isSuperUser) return <NotPermittedPage />;

  const chips = (
    <ChipGroup
      ariaLabel="Date range"
      chips={RANGES.map((r) => ({ id: r.id, label: r.label }))}
      value={rangeId}
      onChange={(id) => setRangeId(id)}
    />
  );
  const openDrill = (d: Drill) => setParams((p) => { p.set('drill', d); return p; });
  const closeDrill = () => setParams((p) => { p.delete('drill'); return p; });

  if (drill) {
    return (
      <Page>
        <PageHeading left={<h1>Dashboard</h1>} right={chips} />
        <PageBody>
          <DrillView drill={drill} range={range} arg={overviewArg} rangeId={rangeId} onBack={closeDrill} />
        </PageBody>
      </Page>
    );
  }

  const empty = !!stats && stats.lines === 0;

  return (
    <Page>
      <PageHeading left={<h1>Dashboard</h1>} right={chips} />
      <PageBody>
        <div className="flex flex-col gap-3 pb-6">
          {overviewLoading || !overview ? <LoadingFlex /> : (
            <div className="grid gap-3 lg:grid-cols-2">
              <FunnelPanel o={overview} range={range} rangeId={rangeId} />
              <FlowPanel o={overview} range={range} arg={overviewArg} onDrill={openDrill} />
            </div>
          )}
          <TodayPanel hourly={overview?.hourly ?? null} opens={overview?.todayOpens ?? 0} asOf={overview?.at ?? null} />

          {loading || !stats ? <LoadingFlex /> : (
            <>
              <StatRow>
                <Stat
                  label="Inventory value"
                  value={inventory ? `${fmtTix(inventory.floorValue)} – ${fmtTix(inventory.askValue)}` : '…'}
                  hint={inventory ? `floor to ask, ${fmtInt(inventory.units)} copies on ${fmtInt(inventory.bots)} bots, as of ${hhmm(inventory.at)}` : 'valuing holdings'}
                />
                <Stat
                  label="Distinct cards"
                  value={fmtInt(stats.distinctCards)}
                  icon="docs"
                  hint={`${fmtInt(stats.distinctPartners)} partners, ${fmtInt(stats.lines)} trade lines`}
                />
                <Stat
                  label="Activity through"
                  value={fmtDate(stats.last)}
                  icon="queryStats"
                  hint={stats.first ? `from ${fmtDate(stats.first)}` : 'no trades in range'}
                />
              </StatRow>

              <Panel
                eyebrow="Buy-side flow"
                title="Cards acquired vs tickets paid"
                actions={<span className="text-[12px] text-fg-3">by {stats.bucket}</span>}
              >
                {empty ? <EmptyRange stats={stats} /> : (
                  <div className="h-72 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={stats.series} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
                        <defs>
                          <linearGradient id="takenFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={theme.primary} stopOpacity={0.35} />
                            <stop offset="100%" stopColor={theme.primary} stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke={theme.grid} vertical={false} />
                        <XAxis dataKey="bucket" tick={{ fill: theme.axis, fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={24} />
                        <YAxis tick={{ fill: theme.axis, fontSize: 11 }} tickLine={false} axisLine={false} width={54} />
                        <ChartTooltip theme={theme} />
                        <Area type="monotone" dataKey="taken" name="Cards taken" stroke={theme.primary} strokeWidth={2} fill="url(#takenFill)" />
                        <Area type="monotone" dataKey="given" name="Tickets paid" stroke={theme.tertiary} strokeWidth={2} fill="none" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Panel>

              <div className="grid gap-3 lg:grid-cols-2">
                <Panel eyebrow="Concentration" title="Most acquired cards">
                  {empty ? <EmptyRange stats={stats} /> : (
                    <div className="h-72 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={stats.topCards} layout="vertical" margin={{ top: 4, right: 12, bottom: 0, left: 8 }}>
                          <CartesianGrid stroke={theme.grid} horizontal={false} />
                          <XAxis type="number" tick={{ fill: theme.axis, fontSize: 11 }} tickLine={false} axisLine={false} />
                          <YAxis type="category" dataKey="card" tick={{ fill: theme.axis, fontSize: 11 }} tickLine={false} axisLine={false} width={130} />
                          <ChartTooltip theme={theme} />
                          <Bar dataKey="units" name="Units" fill={theme.primary} radius={[0, 3, 3, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </Panel>

                {/*
                  Most bots carry no `status` at all -- it is written at runtime --
                  so the list can be dominated by 'unknown'. Showing the denominator
                  makes that read as "103 of 107 have never reported" rather than
                  as a broken query.
                */}
                <Panel
                  eyebrow="Fleet"
                  title="Bots by status"
                  actions={<span className="text-[12px] text-fg-3">{fmtInt(stats.fleet.reduce((n, f) => n + f.count, 0))} total</span>}
                >
                  <div className="flex flex-col gap-2">
                    {stats.fleet.length === 0 && <div className="py-6 text-center text-[13px] text-fg-3">No bots configured</div>}
                    {stats.fleet.map((f) => (
                      <div key={f.status} className="flex items-center justify-between gap-3 border-b border-soft pb-2 last:border-0">
                        <Badge tone={statusTone[f.status] || 'neutral'}>{f.status}</Badge>
                        <span className="num text-[15px] font-semibold text-fg-1">{f.count}</span>
                      </div>
                    ))}
                    {/*
                      Status counts are a photograph; the transition log is the
                      film. A bot that crashes and restarts forty times a day looks
                      identical to a healthy one in the counts above.
                    */}
                    <div className="mt-1 flex items-baseline justify-between gap-3 border-t border-soft pt-2">
                      <span className="eyebrow text-fg-3">Last {reliability?.windowDays ?? 30} days</span>
                      {reliability?.eventCount
                        ? (
                          <span className="text-[12px] text-fg-2">
                            <span className="num font-semibold text-sell">{reliability.crashes}</span> crashes,{' '}
                            <span className="num font-semibold text-warn">{reliability.stalls}</span> stalls,{' '}
                            <span className="num font-semibold text-fg-1">{reliability.restarts}</span> restarts
                          </span>
                        )
                        : <span className="text-[12px] text-fg-3">No transitions recorded yet</span>}
                    </div>
                    {!!reliability?.perBot?.length && (
                      <div className="flex flex-col gap-1">
                        {reliability.perBot
                          .filter((b) => b.crashes + b.stalls + b.restarts > 0)
                          .slice(0, 6)
                          .map((b) => (
                            <div key={b.bot} className="flex items-center justify-between gap-2 text-[12px]">
                              <span className="truncate text-fg-2">{b.bot}</span>
                              <span className="num shrink-0 text-fg-3">
                                {b.crashes > 0 && <span className="text-sell">{b.crashes}c </span>}
                                {b.stalls > 0 && <span className="text-warn">{b.stalls}s </span>}
                                {b.restarts > 0 && <span>{b.restarts}r</span>}
                              </span>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </Panel>
              </div>
            </>
          )}
        </div>
      </PageBody>
    </Page>
  );
};

// ---------------------------------------------------------------- overview ----

const RangeChip = ({ range }: { range: RangeChoice }) => (
  <Badge tone="green"><Icon icon="queryStats" size={11} /> {rangeTitle(range)}</Badge>
);

/** The change against the comparison window: an arrow and a percentage, or
 *  "all time" when there is no window to compare with. */
const PaceDelta = ({ current, prior, label, hasPrior, className }: { current: number; prior: number | null; label: string; hasPrior: boolean; className?: string }) => {
  if (!hasPrior || prior === null) return <span className={`text-[12px] text-fg-3${className ? ` ${className}` : ''}`}>all time</span>;
  const pct = pctDelta(current, prior);
  if (pct === null) return <span className={`text-[12px] text-fg-3${className ? ` ${className}` : ''}`}>no prior {label.replace(/^vs /, '')}</span>;
  const up = pct >= 0;
  return (
    <span className={`num inline-flex items-center gap-1 text-[12px] ${up ? 'text-buy' : 'text-sell'}${className ? ` ${className}` : ''}`}>
      {up ? '▲' : '▼'} {up ? '+' : ''}{Math.round(pct)}% {label}
    </span>
  );
};

const FunnelPanel = ({ o, range, rangeId }: { o: OverviewStats; range: RangeChoice; rangeId: string }) => {
  const conv = o.current.opens ? (o.current.completes / o.current.opens) * 100 : 0;
  const cmp = compareLabel(range.days);
  const blind = o.sessionsSince && o.from && new Date(o.from) < new Date(o.sessionsSince);
  const link = (kind?: string) => `/trade-inspector?range=${rangeId}${kind ? `&kind=${kind}` : ''}`;
  return (
    <Panel eyebrow="Funnel" title="Trade funnel" actions={<RangeChip range={range} />}>
      <div className="mb-4 flex items-end justify-between gap-3">
        <Link to={link()} className="group text-left no-underline">
          <div className="text-[12px] font-semibold text-fg-3 transition-colors group-hover:text-fg-1">Opened trades</div>
          <div className="num mt-1.5 text-[34px] font-bold leading-none tracking-tight text-fg-1">{fmtInt(o.current.opens)}</div>
          <PaceDelta current={o.current.opens} prior={o.prior?.opens ?? null} label={cmp} hasPrior={o.hasPrior} className="mt-1.5" />
        </Link>
        <div className="shrink-0 text-center">
          <div className="num text-[44px] font-bold leading-none tracking-tight text-gold">{fmtPct(conv)}</div>
          <div className="mt-1 text-[11px] text-fg-3">of trades completed</div>
        </div>
        <Link to={link('completed')} className="group text-right no-underline">
          <div className="text-[12px] font-semibold text-fg-3 transition-colors group-hover:text-fg-1">Completed trades</div>
          <div className="num mt-1.5 text-[34px] font-bold leading-none tracking-tight text-fg-1">{fmtInt(o.current.completes)}</div>
          <PaceDelta current={o.current.completes} prior={o.prior?.completes ?? null} label={cmp} hasPrior={o.hasPrior} className="mt-1.5 justify-end" />
        </Link>
      </div>
      <div className="h-3.5 overflow-hidden rounded-sm bg-page-alt">
        <div className="h-full rounded-sm bg-primary" style={{ width: `${conv}%` }} />
      </div>
      {(blind || o.sessionsSince === null) && (
        <div className="mt-2 text-[11px] text-fg-3">
          {o.sessionsSince ? `Trade sessions are recorded since ${fmtDate(o.sessionsSince)}; the funnel is blind before that.` : 'No trade sessions recorded yet.'}
        </div>
      )}
    </Panel>
  );
};

const MoneyBar = ({ verb, cards, tix, pct, color, note, onClick }: { verb: string; cards: number; tix: number; pct: number; color: string; note: string; onClick: () => void }) => (
  <button type="button" onClick={onClick} className="group block w-full cursor-pointer border-none bg-transparent p-0 text-left">
    <div className="mb-1.5 flex items-baseline justify-between">
      <span className="num text-[13px] font-semibold text-fg-2 transition-colors group-hover:text-fg-1">{fmtInt(cards)} cards {verb}</span>
      <span className="num text-[15px] font-bold text-fg-1">{fmtTix(tix)} <span className="text-[11px] font-normal text-fg-3">{note}</span></span>
    </div>
    <div className="h-3 overflow-hidden rounded-sm bg-page-alt">
      <div className={`h-full rounded-sm ${color}`} style={{ width: `${pct}%` }} />
    </div>
  </button>
);

/**
 * Bought against sold, with the net ticket flow and the profit on what we
 * sold. Profit defaults to held cost, like the Trade Inspector; the button
 * walks the whole ledger for the exact LIFO figure and swaps it in.
 */
const FlowPanel = ({ o, range, arg, onDrill }: { o: OverviewStats; range: RangeChoice; arg: OverviewArg; onDrill: (d: Drill) => void }) => {
  const [lifo, setLifo] = useState<TradeProfitLifo | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const c = o.current;
  const max = Math.max(c.tixOut, c.tixIn, 1);
  const net = c.tixIn - c.tixOut;
  const held = c.profit?.value ?? 0;
  const priorProfit = o.prior ? (o.prior.profit?.value ?? 0) : null;
  const profit = lifo ? lifo.profit : held;
  const cmp = compareLabel(range.days);
  const days = range.days === null ? null : Math.max(1, range.days);
  const walk = async () => {
    setBusy(true);
    setErr(null);
    try { setLifo(await getOverviewProfitLifo.call(arg)); }
    catch (e: any) { setErr(e?.reason ?? e?.message ?? 'failed'); }
    finally { setBusy(false); }
  };
  return (
    <Panel eyebrow="Flow" title="Trade flow" actions={<RangeChip range={range} />}>
      <div className="mb-4 grid gap-4">
        <MoneyBar verb="bought" cards={c.cardsBought} tix={c.tixOut} pct={(c.tixOut / max) * 100} color="bg-gold" note="tix out" onClick={() => onDrill('cardsBought')} />
        <MoneyBar verb="sold" cards={c.cardsSold} tix={c.tixIn} pct={(c.tixIn / max) * 100} color="bg-primary" note="tix in" onClick={() => onDrill('cardsSold')} />
      </div>
      <div className="flex flex-wrap items-end justify-between gap-3 border-t border-soft pt-3.5">
        <div>
          <span className="text-[13px] text-fg-3">Net flow</span>
          <span className={`num ml-1.5 font-bold ${net >= 0 ? 'text-buy' : 'text-sell'}`}>{fmtSigned(net)}</span>
          <div className="mt-1 text-[11px] text-fg-3">sold minus bought; negative means we are net buying stock</div>
        </div>
        <div className="text-right">
          <button type="button" onClick={() => onDrill('profit')} className="group cursor-pointer border-none bg-transparent p-0 text-right">
            <span className="text-[13px] text-fg-3 transition-colors group-hover:text-fg-1">Profit</span>
            <span className={`num ml-1.5 text-[18px] font-bold ${profit > 0 ? 'text-buy' : profit < 0 ? 'text-sell' : 'text-fg-1'}`}>{fmtSigned(profit)}</span>
          </button>
          <div className="mt-1 flex flex-wrap items-center justify-end gap-2">
            {lifo
              ? <span className="num text-[12px] text-fg-3">exact LIFO · cost {fmtPrice(lifo.cogs)}{lifo.unmatchedUnits ? ` · ${fmtInt(lifo.unmatchedUnits)} unmatched` : ''}</span>
              : <PaceDelta current={held} prior={priorProfit} label={cmp} hasPrior={o.hasPrior} />}
            <span className="text-[12px] text-fg-3">
              {o.isToday
                ? `on pace for ${fmtTix(profit / o.dayFraction)}`
                : days ? `daily avg ${fmtTix(profit / days)}` : ''}
            </span>
            {!lifo && (c.profit?.units ?? 0) > 0 && (
              <button type="button" onClick={walk} disabled={busy} title="Walk the whole ledger for the exact lots these sales consumed" className="inline-flex cursor-pointer items-center gap-1 rounded border border-soft bg-transparent px-1.5 py-0.5 text-[10px] font-semibold text-fg-3 hover:border-mid hover:text-fg-1 disabled:cursor-wait">
                <Icon icon="layers" size={11} /> {busy ? 'walking…' : 'exact LIFO'}
              </button>
            )}
          </div>
          <div className="mt-0.5 text-[11px] text-fg-3">
            {err ? <span className="text-sell">{err}</span>
              : lifo ? 'on the lots each sale actually consumed'
                : c.profit ? `held cost on ${fmtInt(c.profit.units)} sold${c.profit.estimatedUnits ? `, ${fmtInt(c.profit.estimatedUnits)} est.` : ''}${c.profit.unknownUnits ? `, ${fmtInt(c.profit.unknownUnits)} uncosted` : ''}`
                  : 'nothing sold in this window'}
          </div>
        </div>
      </div>
    </Panel>
  );
};

const HOUR_LABELS = ['12a', '6a', '12p', '6p', '11p'];

/** The live pulse of the day: opens by local hour, then the newest trades. */
const TodayPanel = ({ hourly, opens, asOf }: { hourly: number[] | null; opens: number; asOf: Date | null }) => {
  const navigate = useNavigate();
  const [feed, loading] = useMethod(getTradeLog, { from: localMidnight(), limit: 12, sort: 'recent' as const }, []);
  const rows = feed?.rows ?? [];
  const maxH = Math.max(...(hourly ?? [0]), 1);
  const nowHour = new Date().getHours();
  const lastActive = (hourly ?? []).reduce((acc, v, i) => (v > 0 ? i : acc), 0);
  return (
    <Panel
      flush
      eyebrow="Today"
      title="Today’s trades"
      actions={
        <span className="flex items-center gap-3">
          <span className="num text-[12px] text-fg-3">{fmtInt(opens)} opened today</span>
          <Link to="/trade-inspector?range=today" className="text-[12px] font-semibold text-primary no-underline hover:underline">View all</Link>
        </span>
      }
    >
      <div className="border-b border-soft px-4 py-3">
        <div className="flex items-baseline justify-between gap-2">
          <span className="eyebrow text-fg-3">Opens by hour</span>
          <span className="num text-[10px] text-fg-3">{asOf ? `as of ${hhmm(asOf)}` : ''}</span>
        </div>
        <div className="mt-1.5 flex h-9 items-end gap-[3px]">
          {(hourly ?? new Array(24).fill(0)).map((v, h) => {
            const future = h > nowHour;
            return (
              <div key={h} className="flex h-full flex-1 items-end" title={future ? `${h}:00 — not yet` : `${h}:00 — ${v} opened`}>
                {future
                  ? <div className="h-px w-full rounded-full bg-soft" />
                  : <div className={`w-full rounded-t-[2px] ${h === lastActive ? 'bg-primary' : 'bg-primary/60'}`} style={{ height: `${v ? Math.max(6, (v / maxH) * 100) : 0}%` }} />}
              </div>
            );
          })}
        </div>
        <div className="num mt-1 flex justify-between text-[9px] text-fg-3">
          {HOUR_LABELS.map((l) => <span key={l}>{l}</span>)}
        </div>
      </div>
      <div className="overflow-x-auto">
        {loading ? <LoadingFlex /> : rows.length === 0 ? (
          <div className="py-10 text-center text-[13px] text-fg-3">No trades opened yet today</div>
        ) : (
          <table className="w-full min-w-[580px] border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-soft">
                {['Time', 'Counterparty', 'Bot', 'Flow', 'Cards', 'Tix', 'Outcome', ''].map((h, i) => (
                  <th key={h || 'chev'} className={`eyebrow px-2 py-2 text-fg-3 ${i === 4 || i === 5 ? 'text-right' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => <FeedRow key={s._id} s={s} onOpen={() => navigate(`/trade-inspector?t=${s._id}`)} />)}
            </tbody>
          </table>
        )}
      </div>
    </Panel>
  );
};

const FeedRow = ({ s, onOpen }: { s: TradeSessionRow; onOpen: () => void }) => {
  const k = kindVocab(s.kind);
  const cards = s.cardsIn + s.cardsOut;
  const tix = s.tixIn + s.tixOut;
  return (
    <tr onClick={onOpen} className="group cursor-pointer border-t border-soft/60 hover:bg-page-alt">
      <td className="num whitespace-nowrap px-3 py-2 text-[12px] text-fg-2">{hhmm(s.at)} <span className="text-fg-3">· {agoShort(s.at)}</span></td>
      <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}><Link to={`/musers/${s.partnerId}`} className="num text-[13px] text-fg-1 no-underline hover:underline">{s.partner}</Link></td>
      <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
        {s.botDocId ? <Link to={`/bots/${s.botDocId}`} className="num text-[12px] text-fg-2 no-underline hover:underline">{s.bot}</Link> : <span className="num text-[12px] text-fg-2">{s.bot}</span>}
      </td>
      <td className="px-2 py-2 text-[12px] text-fg-2">{s.creditPayout ? 'Credit payout' : s.direction !== 'none' ? DIR_LABEL[s.direction] : <span className="text-fg-3">no deal</span>}</td>
      <td className="num px-2 py-2 text-right text-[12px] text-fg-2">{cards || '–'}</td>
      <td className="num px-2 py-2 text-right text-[12px] text-fg-1">{tix ? fmtInt(Math.round(tix)) : '–'}</td>
      <td className="px-2 py-2"><Badge tone={k.tone}>{k.label}</Badge>{s.initiatedByUs && <Badge tone="gold" className="ml-1">we invited</Badge>}</td>
      <td className="py-2 pr-3 text-right"><Icon icon="chevronRight" size={14} className="inline text-fg-3 opacity-0 group-hover:opacity-100" /></td>
    </tr>
  );
};

// ---------------------------------------------------------------- drills ----

const DrillView = ({ drill, range, arg, rangeId, onBack }: { drill: Drill; range: RangeChoice; arg: OverviewArg; rangeId: string; onBack: () => void }) => {
  const scope = rangeTitle(range).toLowerCase();
  const title = { profit: `Profit by card · ${scope}`, cardsBought: `Cards bought · ${scope}`, cardsSold: `Cards sold · ${scope}` }[drill];
  return (
    <div className="flex flex-col gap-3 pb-6">
      <button type="button" onClick={onBack} className="inline-flex cursor-pointer items-center gap-1.5 self-start border-none bg-transparent p-0 text-[13px] font-semibold text-fg-3 hover:text-fg-1">
        <Icon icon="chevronLeft" size={15} /> Dashboard
      </button>
      <h2 className="m-0 text-[22px] font-bold leading-tight text-fg-1">{title}</h2>
      {drill === 'profit' ? <ProfitByCardTable arg={arg} rangeId={rangeId} /> : <CardLinesTable side={drill === 'cardsSold' ? 'sell' : 'buy'} arg={arg} rangeId={rangeId} />}
    </div>
  );
};

const Th = ({ children, right }: { children: React.ReactNode; right?: boolean }) => (
  <th className={`eyebrow px-2 py-2 text-fg-3 ${right ? 'text-right' : 'text-left'}`}>{children}</th>
);

const ProfitByCardTable = ({ arg, rangeId }: { arg: OverviewArg; rangeId: string }) => {
  const [data, loading] = useMethod(getProfitByCard, arg, [rangeId]);
  const [q, setQ] = useState('');
  const rows = (data?.rows ?? []).filter((r) => !q || r.card.toLowerCase().includes(q.toLowerCase()));
  const totals = rows.reduce((a, r) => ({ units: a.units + r.units, profit: a.profit + r.profit, est: a.est + r.estimatedUnits }), { units: 0, profit: 0, est: 0 });
  return (
    <Panel flush actions={<div className="w-64"><Input value={q} onChange={(v) => setQ(v ?? '')} placeholder="Search card" /></div>} title={`${fmtInt(rows.length)} cards · ${fmtInt(totals.units)} sold · ${fmtSigned(totals.profit)}${totals.est ? ` · ${fmtInt(totals.est)} est.` : ''}`}>
      {loading ? <LoadingFlex /> : rows.length === 0 ? <div className="py-10 text-center text-[13px] text-fg-3">Nothing sold in this window</div> : (
        <div className="overflow-x-auto px-2 py-2">
          <table className="w-full border-collapse text-[13px]">
            <thead><tr><Th>Card</Th><Th>Set</Th><Th right>Trades</Th><Th right>Sold</Th><Th right>Proceeds</Th><Th right>Cost</Th><Th right>Profit</Th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.cardId} className="border-t border-soft/60">
                  <td className="px-2 py-1.5"><Link to={`/cards/${r.cardId}`} className="text-fg-1 no-underline hover:underline">{r.card}</Link>{r.foil && <Badge tone="gold" className="ml-1">F</Badge>}</td>
                  <td className="num px-2 py-1.5 text-[11px] uppercase text-fg-3">{r.set}</td>
                  <td className="num px-2 py-1.5 text-right text-fg-2">{fmtInt(r.trades)}</td>
                  <td className="num px-2 py-1.5 text-right text-fg-2">{fmtInt(r.units)}</td>
                  <td className="num px-2 py-1.5 text-right text-fg-2">{fmtPrice(r.proceeds)}</td>
                  <td className="num px-2 py-1.5 text-right text-fg-2" title={r.estimatedUnits ? `${r.estimatedUnits} costed from the buy book` : undefined}>{fmtPrice(r.cost)}{r.estimatedUnits ? '*' : ''}</td>
                  <td className={`num px-2 py-1.5 text-right font-semibold ${r.profit > 0 ? 'text-buy' : r.profit < 0 ? 'text-sell' : 'text-fg-1'}`}>{fmtSigned(r.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
};

const CardLinesTable = ({ side, arg, rangeId }: { side: 'buy' | 'sell'; arg: OverviewArg; rangeId: string }) => {
  const navigate = useNavigate();
  const [data, loading] = useMethod(getCardLines, { ...arg, side, limit: 500 }, [rangeId, side]);
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const rows = (data?.rows ?? []).filter((r) => !needle || r.card.toLowerCase().includes(needle) || r.partner.toLowerCase().includes(needle));
  return (
    <Panel
      flush
      title={`${fmtInt(rows.length)} lines${data && data.total > data.rows.length ? ` of ${fmtInt(data.total)}, newest first` : ''}`}
      actions={<div className="w-64"><Input value={q} onChange={(v) => setQ(v ?? '')} placeholder="Search card or counterparty" /></div>}
    >
      {loading ? <LoadingFlex /> : rows.length === 0 ? <div className="py-10 text-center text-[13px] text-fg-3">Nothing here</div> : (
        <div className="overflow-x-auto px-2 py-2">
          <table className="w-full border-collapse text-[13px]">
            <thead><tr><Th>Time</Th><Th>Card</Th><Th>Counterparty</Th><Th>Bot</Th><Th right>Qty</Th><Th right>Price</Th><Th right>Total</Th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r._id} onClick={() => r.sessionId && navigate(`/trade-inspector?t=${r.sessionId}`)} className={`border-t border-soft/60 ${r.sessionId ? 'cursor-pointer hover:bg-page-alt' : ''}`}>
                  <td className="num whitespace-nowrap px-2 py-1.5 text-[12px] text-fg-2">{r.at ? `${ymd(r.at)} ${hhmm(r.at)}` : '–'}</td>
                  <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                    <Link to={`/cards/${r.cardId}`} className="text-fg-1 no-underline hover:underline">{r.card}</Link>
                    <span className="num ml-1.5 text-[11px] uppercase text-fg-3">{r.set}</span>
                    {r.foil && <Badge tone="gold" className="ml-1">F</Badge>}
                  </td>
                  <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                    <span className="text-fg-3">{side === 'sell' ? 'to ' : 'from '}</span>
                    <Link to={`/musers/${r.partnerId}`} className="num text-fg-1 no-underline hover:underline">{r.partner}</Link>
                  </td>
                  <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                    {r.botDocId ? <Link to={`/bots/${r.botDocId}`} className="num text-[12px] text-fg-2 no-underline hover:underline">{r.bot}</Link> : <span className="num text-[12px] text-fg-2">{r.bot}</span>}
                  </td>
                  <td className="num px-2 py-1.5 text-right text-fg-2">{r.qty}x</td>
                  <td className="num px-2 py-1.5 text-right text-fg-2">{fmtPrice(r.price)}{r.currency === 'usd' ? ' $' : ''}</td>
                  <td className="num px-2 py-1.5 text-right text-fg-1">{r.price == null ? '–' : fmtPrice(r.price * r.qty)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
};
